import { useEffect, useMemo, useState } from 'react';
import {
  fetchSmartImportPreview,
  fetchSmartPatient,
  getStoredSmartHistoryWindow,
  getStoredSmartWorkspacePatient,
  normalizeSmartClinicalPreview,
  normalizeSmartPatient,
  resolveSmartSessionIdFromLocation,
  storeSmartWorkspacePatient,
} from '../api/smartApi';
import type { SmartPatientContextState, SmartPatientDemographics } from '../types/smart';

const INITIAL: SmartPatientContextState = {
  status: 'idle',
  sessionId: null,
  patient: null,
  preview: null,
  error: null,
  loadedAt: null,
};

function storedWorkspaceToPatient(): SmartPatientDemographics | null {
  const stored = getStoredSmartWorkspacePatient();
  if (!stored?.mrn) return null;
  return {
    fhirId: stored.mrn,
    displayName: stored.name || 'Current patient',
    mrn: stored.mrn,
    accountNumber: '',
    gender: '',
    birthDate: '',
    ageYears: null,
    active: stored.status ? stored.status.toLowerCase() !== 'inactive' : null,
    phone: '',
    email: '',
    address: '',
    managingOrganization: '',
  };
}

export type SmartPatientContextOptions = {
  /**
   * Also fetch the clinical preview. OFF by default, and that default is the
   * point — see the comment on the fetch below. Only the patient drawer reads
   * `preview`, so only the patient drawer asks for it.
   */
  withPreview?: boolean;
};

export function useSmartPatientContext(
  options?: SmartPatientContextOptions,
): SmartPatientContextState & { available: boolean } {
  const withPreview = options?.withPreview ?? false;
  const [state, setState] = useState<SmartPatientContextState>(INITIAL);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const sessionId = resolveSmartSessionIdFromLocation();
    const storedPatient = storedWorkspaceToPatient();
    if (!sessionId) {
      if (storedPatient) {
        setState({
          status: 'ready',
          sessionId: null,
          patient: storedPatient,
          preview: null,
          error: 'Reconnect the patient record to refresh clinical details.',
          loadedAt: null,
        });
      } else {
        setState(INITIAL);
      }
      return undefined;
    }

    let cancelled = false;
    setState({
      ...INITIAL,
      status: storedPatient ? 'ready' : 'loading',
      sessionId,
      patient: storedPatient,
      error: storedPatient ? 'Refreshing patient details…' : null,
    });

    // Re-send the session's own window. This hook only DISPLAYS the patient
    // (patient drawer, Digital Twin page, Trends page), but the preview
    // endpoint it calls is also the one that records the window — so omitting
    // it here used to widen the whole session to full history the moment a
    // clinician opened any of those pages (measured live 2026-08-01: badge
    // "10 YEARS", /encounters back to 1969). The server now treats an absent
    // window as "no change", and this sends the real value anyway so the
    // intent is explicit at the call site rather than resting on that default.
    const activeWindow = getStoredSmartHistoryWindow() ?? undefined;
    // THE PREVIEW CALL IS A FULL RE-IMPORT, so it is opt-in.
    //
    // `/api/smart/import/preview` is not a preview. Its handler says so in as
    // many words (routes.py smart_import_preview: "this endpoint IS the real
    // import -- import_phase=True is unconditional"), and it re-persists the
    // whole chart NDJSON every time it is called.
    //
    // Calling it on EVERY mount of this hook made the chart page slow in a way
    // that looked like a hang. Re-persisting the NDJSON makes every source file
    // newer than the persisted chart store, so `_try_open_fresh_persisted`
    // rejects the cache by mtime and the next chart request rebuilds the whole
    // thing. Measured live on dev, three occasions: 43s, 23s and 16s from the
    // cache-miss log line to `session_chart_warm_ok`. Worse, it was
    // self-inflicted across pages — opening the Digital Twin invalidated the
    // cache that the Trend Visualization page then had to wait to rebuild on
    // the way back.
    //
    // A fresh launch is unaffected: `SmartEntryGate` fires the real import
    // itself and already treats it as a long request.
    const previewRequest = withPreview
      ? fetchSmartImportPreview(sessionId, activeWindow)
      : Promise.resolve(null);
    Promise.allSettled([fetchSmartPatient(sessionId), previewRequest])
      .then(([patientResult, previewResult]) => {
        if (cancelled) return;
        if (patientResult.status !== 'fulfilled') {
          const message = patientResult.reason instanceof Error
            ? patientResult.reason.message
            : 'Unable to load patient details.';
          const fallbackPatient = storedWorkspaceToPatient();
          if (fallbackPatient) {
            setState({
              status: 'ready',
              sessionId,
              patient: fallbackPatient,
              preview: null,
              error: message,
              loadedAt: null,
            });
            return;
          }
          setState({ ...INITIAL, status: 'error', sessionId, error: message });
          return;
        }

        const patient = normalizeSmartPatient(patientResult.value);
        // `null` is the skipped-preview sentinel, not a failed one — a consumer
        // that did not ask for the preview must not be handed a normalized
        // empty shape that reads like "the chart came back with nothing".
        const preview = previewResult.status === 'fulfilled' && previewResult.value !== null
          ? normalizeSmartClinicalPreview(previewResult.value)
          : null;

        storeSmartWorkspacePatient(patient);
        window.dispatchEvent(new CustomEvent('march-smart-patient-ready', { detail: { source: 'smart-context' } }));

        setState({
          status: 'ready',
          sessionId,
          patient,
          preview,
          error: withPreview && previewResult.status === 'rejected'
            ? 'Patient details loaded, but the clinical summary is temporarily unavailable.'
            : null,
          loadedAt: new Date().toISOString(),
        });
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Unable to load patient details.';
        const fallbackPatient = storedWorkspaceToPatient();
        if (fallbackPatient) {
          setState({
            status: 'ready',
            sessionId,
            patient: fallbackPatient,
            preview: null,
            error: message,
            loadedAt: null,
          });
          return;
        }
        setState({ ...INITIAL, status: 'error', sessionId, error: message });
      });

    return () => {
      cancelled = true;
    };
  }, [reloadKey, withPreview]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const reload = (event?: Event) => {
      const source = event instanceof CustomEvent ? event.detail?.source : null;
      if (source === 'smart-context') return;
      setReloadKey((key) => key + 1);
    };
    // Section J (per-tab scoping): react only to in-tab patient changes
    // (the custom event). The cross-tab `storage` event is intentionally NOT
    // listened to — opening a different patient in another tab must never switch
    // or reload the patient active in this tab. Active patient identity is
    // per-tab via sessionStorage (see smartApi.getStoredSmartWorkspacePatient).
    window.addEventListener('march-smart-patient-ready', reload);
    return () => {
      window.removeEventListener('march-smart-patient-ready', reload);
    };
  }, []);

  return useMemo(
    () => ({ ...state, available: state.status === 'ready' && !!state.patient }),
    [state],
  );
}
